// Shared design tokens for the internal engineering tool suite.
//
// SOURCE OF TRUTH: this file lives in the EngineeringTools repository. The
// EventManager frontend keeps a byte-identical copy at
// event-manager/src/theme/appTheme.ts, and VehicleDebugger mirrors the same
// values as CSS custom properties in public/style.css. When you change a value
// here, carry it into both siblings in the same change set.
//
// The palette is deliberately monochrome: primary is near-black in light mode
// and near-white in dark mode. Semantic colours (success / warning / error /
// info) stay chromatic so status still reads at a glance, and they are pinned
// to explicit values rather than inherited from MUI's defaults so the three
// tools cannot drift apart when their MUI versions differ.

import { createTheme, type Theme } from '@mui/material'

export type ThemeMode = 'light' | 'dark'

export const palette = {
  light: {
    primary: '#000000',
    primaryContrast: '#ffffff',
    backgroundDefault: '#f7f7f7',
    backgroundPaper: '#ffffff',
    // Recessed surface behind header controls (the header's theme button and language
    // switcher sit on it). Mirrors VehicleDebugger's --surface-muted.
    surfaceMuted: '#f2f2f2',
    textPrimary: '#0a0a0a',
    textSecondary: '#5f5f5f',
    divider: '#e2e2e2',
    // A step stronger than divider, used for hover borders on those controls.
    borderStrong: '#b8b8b8',
    headerShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
    success: '#2e7d32',
    warning: '#ed6c02',
    error: '#d32f2f',
    info: '#0288d1',
  },
  dark: {
    primary: '#ffffff',
    primaryContrast: '#000000',
    backgroundDefault: '#0a0a0a',
    backgroundPaper: '#151515',
    surfaceMuted: '#1f1f1f',
    textPrimary: '#fafafa',
    textSecondary: '#a3a3a3',
    divider: '#2c2c2c',
    borderStrong: '#4a4a4a',
    headerShadow: '0 1px 3px rgba(0, 0, 0, 0.35)',
    success: '#66bb6a',
    warning: '#ffa726',
    error: '#f44336',
    info: '#29b6f6',
  },
} as const

/** Neutral status dots, used where a chromatic MUI severity would be too loud. */
export const statusDotColors = {
  neutral: '#9ca3af',
  unknown: '#6b7280',
  ok: '#15803d',
  down: '#b91c1c',
} as const

export const fontFamily = '"Segoe UI", Arial, sans-serif'

export const borderRadius = 14

/**
 * Header geometry, taken value-for-value from VehicleDebugger's `.site-header` block.
 * VehicleDebugger is the reference for the header: the brand lockup on the left (logo, rule,
 * product name over tagline) and the controls on the right (theme button, then language
 * switcher).
 *
 * Expressed in the same rem/px units as that CSS rather than in MUI spacing multiples —
 * MUI's 8px grid cannot hit 0.35rem, and approximating it made the language switcher 5px
 * shorter and 29px narrower than VehicleDebugger's.
 */
export const headerTokens = {
  /** .brand-logo height, and its mobile step-down. */
  logoHeight: 42,
  logoHeightMobile: 36,
  /** .header-inner max-width. Deliberately narrower than the page content below, as in
   *  VehicleDebugger, so the header is identical across tools regardless of their own
   *  content width. */
  maxWidth: 1040,
  /** .header-inner gap and .header-actions gap. */
  gap: '1.25rem',
  actionsGap: '0.75rem',
  /** .site-header padding. */
  paddingY: '1rem',
  paddingX: '1.5rem',
  /**
   * Height of the row holding the brand lockup and the controls, pinned so the header cannot
   * change height with its contents. 73px is VehicleDebugger's tallest case — its signed-in
   * user block (label, name, email). Without the pin, VehicleDebugger's own header is 106px
   * signed in and 83px signed out, and the other tools land somewhere else again.
   */
  contentMinHeight: '73px',
  /** .product-name / .product-tagline. */
  nameFontSize: '1.125rem',
  nameFontWeight: 700,
  nameLetterSpacing: '-0.02em',
  taglineFontSize: '0.875rem',
  taglineMarginTop: '0.15rem',
  /** .theme-btn is a 2.2rem square. */
  controlSize: '2.2rem',
  /** var(--radius-md) on the theme button and the language switcher shell. */
  controlRadius: '10px',
  /** var(--radius-sm) on the individual language buttons. */
  chipRadius: '6px',
  controlFontSize: '0.8rem',
  /** .lang-switcher shell, .lang-label spacing and .lang-btn padding. */
  switcherPadding: '0.35rem 0.5rem',
  switcherGap: '0.35rem',
  switcherLabelGap: '0.25rem',
  chipPadding: '0.35rem 0.65rem',
} as const

/**
 * Footer geometry, taken from VehicleDebugger's `.site-footer`. Same width as the header so
 * the two rails line up, and the same centred wrapping row of muted separator-joined parts.
 */
export const footerTokens = {
  maxWidth: headerTokens.maxWidth,
  padding: '1.5rem',
  fontSize: '0.8rem',
  /** Row gap / column gap of the wrapping parts. */
  gap: '0.35rem 0.5rem',
  /** Separator between the parts inside one span. */
  separator: ' · ',
} as const

/**
 * Resolve the mode to start in: an explicit earlier choice wins, otherwise
 * follow the operating system. `storageKey` differs per tool because the tools
 * are served from different origins and each already has stored preferences.
 */
export function resolveInitialMode(storageKey: string): ThemeMode {
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // Private modes and blocked site data throw on access; fall through to the OS.
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function createAppTheme(mode: ThemeMode): Theme {
  const colors = palette[mode]
  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary,
        contrastText: colors.primaryContrast,
      },
      background: {
        default: colors.backgroundDefault,
        paper: colors.backgroundPaper,
      },
      text: {
        primary: colors.textPrimary,
        secondary: colors.textSecondary,
      },
      divider: colors.divider,
      success: { main: colors.success },
      warning: { main: colors.warning },
      error: { main: colors.error },
      info: { main: colors.info },
    },
    shape: { borderRadius },
    typography: {
      fontFamily,
      h3: { fontWeight: 750, letterSpacing: '-0.035em' },
      h5: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 650 },
      overline: { letterSpacing: '0.16em' },
    },
  })
}
